'use client';

import React, { useCallback, useState } from 'react';
import { WordpressSidebar } from '@benzinga/content-manager';
import { ArticleBlock as ArticleBlockType } from '@benzinga/article-manager';
import { NoFirstRender } from '@benzinga/hooks';
import { isDisplayAdBlockName, type FreestarPlacement } from '@benzinga/ads-utils';
import { RecommendedArticles } from './RecommendedArticles';
import { SequentialStickyStack } from './SequentialStickyRail';

const Blocks = React.lazy(() => import('@benzinga/blocks').then(module => ({ default: module.Blocks })));
const LayoutAdmin = React.lazy(() => import('@benzinga/blocks').then(module => ({ default: module.LayoutAdmin })));

const FreestarAd = React.lazy(() =>
  import('@benzinga/ads/freestar').then(module => {
    return { default: module.FreestarAd };
  }),
);

export interface ArticleSidebarProps {
  hasAdLight?: boolean;
  followUpQuestions?: string[] | null;
  raptiveEnabled?: boolean;
  nodeId?: number;
  sidebar?: WordpressSidebar;
}

export const ArticleSidebar: React.FC<ArticleSidebarProps> = ({ hasAdLight, nodeId, raptiveEnabled, sidebar }) => {
  const [trackedNodeId, setTrackedNodeId] = useState(nodeId);
  const [recommendedStatus, setRecommendedStatus] = useState<'pending' | 'ready' | 'empty'>('pending');
  const handleHasStories = useCallback((hasStories: boolean) => {
    setRecommendedStatus(hasStories ? 'ready' : 'empty');
  }, []);

  if (nodeId !== trackedNodeId) {
    setTrackedNodeId(nodeId);
    setRecommendedStatus('pending');
  }

  const hasExistingPlacement = (placement: FreestarPlacement) =>
    !!sidebar?.blocks?.find(block => isDisplayAdBlockName(block.blockName) && block.attrs?.data?.type === placement);

  const hasExistingStaticSidebarPlacement = hasExistingPlacement('sidebar_1');
  const hasExistingStickySidebarPlacement = hasExistingPlacement('sidebar_2');
  const stackEnabled = Boolean(raptiveEnabled && !hasAdLight);

  return (
    <div className="flex h-full flex-col gap-4">
      <NoFirstRender>
        <React.Suspense>{sidebar && <LayoutAdmin post={sidebar} />}</React.Suspense>
      </NoFirstRender>
      <SequentialStickyStack enabled={stackEnabled}>
        {!hasExistingStaticSidebarPlacement && raptiveEnabled && (
          <React.Suspense fallback={<div className="min-h-[250px] w-full" />}>
            <FreestarAd className="w-[300px] overflow-hidden" placement="sidebar_1" />
          </React.Suspense>
        )}
        {nodeId && recommendedStatus !== 'empty' && (
          <RecommendedArticles articleId={nodeId as number} onHasStories={handleHasStories} />
        )}
        {sidebar?.blocks && (
          <React.Suspense fallback={null}>
            <Blocks
              blocks={sidebar.blocks as unknown as ArticleBlockType[]}
              className="flex flex-col gap-4"
              type="sidebar"
            />
          </React.Suspense>
        )}
        {!hasExistingStickySidebarPlacement && raptiveEnabled && !hasAdLight && (
          <React.Suspense fallback={<div className="min-h-[250px] w-full" />}>
            <FreestarAd className="w-[300px]" placement="sidebar_2" />
          </React.Suspense>
        )}
      </SequentialStickyStack>
    </div>
  );
};
